import { registerSchema, type InvitePreviewDto } from '@vmt/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Field';
import { FullPageSpinner } from '../components/ui/Spinner';
import { api, ApiError, errorMessage } from '../lib/api';
import { formatDateTime, ROLE_LABEL } from '../lib/format';

export function RegisterPage() {
  const [params] = useSearchParams();
  const token = params.get('invite') ?? '';
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', email: '', password: '', confirm: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<InvitePreviewDto>(`/auth/invites/${encodeURIComponent(token)}`),
    enabled: token.length > 0,
    retry: false,
  });

  if (!token)
    return (
      <EmptyState
        title="Einladung fehlt"
        description="Registrieren ist nur mit einem Einladungslink möglich."
      />
    );
  if (invite.isLoading) return <FullPageSpinner />;
  if (invite.isError) {
    return (
      <EmptyState
        title="Einladung ungültig"
        description="Der Link ist abgelaufen, wurde bereits verwendet oder ist falsch."
      />
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const fieldErrors: Record<string, string[]> = {};
    if (form.password !== form.confirm) fieldErrors.confirm = ['Passwörter stimmen nicht überein'];
    const parsed = registerSchema.safeParse({
      inviteToken: token,
      username: form.username,
      email: form.email,
      password: form.password,
    });
    if (!parsed.success) {
      for (const issue of parsed.error.issues)
        (fieldErrors[String(issue.path[0])] ??= []).push(issue.message);
    }
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length || !parsed.success) return;

    setPending(true);
    try {
      await register(parsed.data);
      navigate('/profile', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) setErrors(err.details);
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm pt-8">
      <h1 className="mb-2 text-center text-4xl">
        Regis<span className="text-accent">trieren</span>
      </h1>
      <p className="mb-6 text-center text-sm text-ink-400">
        Einladung als <strong className="text-bone">{ROLE_LABEL[invite.data!.role]}</strong>
        {invite.data!.playerDisplayName && (
          <>
            {' '}
            für Spieler <strong className="text-bone">{invite.data!.playerDisplayName}</strong>
          </>
        )}{' '}
        · gültig bis {formatDateTime(invite.data!.expiresAt)}
      </p>
      <Card>
        <CardBody>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <Input
              label="Benutzername"
              autoComplete="username"
              value={form.username}
              error={errors.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
            <Input
              label="E-Mail"
              type="email"
              autoComplete="email"
              value={form.email}
              error={errors.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Input
              label="Passwort"
              type="password"
              autoComplete="new-password"
              hint="Mindestens 10 Zeichen"
              value={form.password}
              error={errors.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <Input
              label="Passwort bestätigen"
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              error={errors.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            />
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" loading={pending}>
              Account erstellen
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
